/**
 * Dwell + hysteresis over the guest's `playoutLatencyTrendMs` before the ladder may read it.
 *
 * Field shape (Android, 2026-09-19, session 84382f4a): the trend swung 213 -> 41 -> 181 -> 20 ms
 * tick to tick, and two consecutive ticks at 137 and 154 ms were enough for the ladder to
 * return `elevated / playout-trend`, cut the cap and raise a sticky floor - on a 5 ms LAN with
 * zero loss. A single tick is not a trend. The signal must sit above its threshold for
 * PLAYOUT_TREND_DWELL_MS continuously before the ladder sees it at all, and once it has
 * fired it stays visible until it falls PLAYOUT_TREND_HYSTERESIS_MS below the threshold, so
 * an oscillation around the threshold neither re-triggers nor flickers.
 *
 * Applies uniformly (Android and desktop). Safari never reaches this gate: its capability
 * yields `signalAvailabilityFor(...).playoutTrend === false` and the ladder ignores the
 * field outright (streamCongestionSignal.ts).
 *
 * Browser-free and Node/Jest-safe.
 */

import { PLAYOUT_TREND_DWELL_MS, PLAYOUT_TREND_HYSTERESIS_MS } from './connectSettleContracts';

type PeerTrendState = {
  /** When the trend first exceeded the threshold in the current run, or null while below. */
  aboveSinceMs: number | null;
  /** True once the dwell has completed; stays true until the hysteresis release. */
  passing: boolean;
};

export type PlayoutTrendGateOptions = {
  dwellMs?: number;
  hysteresisMs?: number;
  nowMs?: () => number;
};

export class PlayoutTrendGate {
  private readonly dwellMs: number;
  private readonly hysteresisMs: number;
  private readonly nowMs: () => number;
  private readonly peers = new Map<string, PeerTrendState>();

  constructor(opts: PlayoutTrendGateOptions = {}) {
    this.dwellMs = opts.dwellMs ?? PLAYOUT_TREND_DWELL_MS;
    this.hysteresisMs = opts.hysteresisMs ?? PLAYOUT_TREND_HYSTERESIS_MS;
    this.nowMs = opts.nowMs ?? (() => Date.now());
  }

  /**
   * Returns the trend value the ladder may see: the raw value once it has dwelled above
   * `elevatedThresholdMs` (and until it drops the hysteresis below it), else undefined.
   *
   * An absent or non-finite reading clears the peer's run: a guest that stopped measuring
   * has no trend, and the next reading starts the dwell from scratch.
   */
  public gate(peerId: string, trendMs: number | undefined, elevatedThresholdMs: number): number | undefined {
    if (typeof trendMs !== 'number' || !Number.isFinite(trendMs)) {
      this.peers.delete(peerId);
      return undefined;
    }
    const state = this.stateFor(peerId);
    const now = this.nowMs();

    if (state.passing) {
      if (trendMs < elevatedThresholdMs - this.hysteresisMs) {
        state.passing = false;
        state.aboveSinceMs = null;
        return undefined;
      }
      return trendMs;
    }

    if (trendMs < elevatedThresholdMs) {
      state.aboveSinceMs = null;
      return undefined;
    }
    if (state.aboveSinceMs === null) state.aboveSinceMs = now;
    if (now - state.aboveSinceMs < this.dwellMs) return undefined;
    state.passing = true;
    return trendMs;
  }

  /** True while the peer's trend has dwelled and not yet released — for debug lines. */
  public isPassing(peerId: string): boolean {
    return this.peers.get(peerId)?.passing ?? false;
  }

  public clearPeer(peerId: string): void {
    this.peers.delete(peerId);
  }

  public clearAll(): void {
    this.peers.clear();
  }

  private stateFor(peerId: string): PeerTrendState {
    let state = this.peers.get(peerId);
    if (!state) {
      state = { aboveSinceMs: null, passing: false };
      this.peers.set(peerId, state);
    }
    return state;
  }
}
